import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TERMS_VERSION } from '@studio/shared';

import { auth } from '../lib/api';
import { useDateFormat } from '../lib/dates';
import { useI18n } from '../lib/i18n';

type Kind = 'terms' | 'privacy';
type Who = { operator: string; contact: string; host: string };
type Section = { title: string; body: string[] };

/**
 * The terms of use and the privacy notice.
 *
 * Public, like a review link: someone deciding whether to accept an
 * invitation reads them before they have an account. Who runs the studio and
 * how to reach them come from the server's environment, so the operator fills
 * in three variables rather than editing this file.
 *
 * These are a starting point written for this service — a private beta of an
 * image tool that handles photographs of people — not legal advice. The
 * operator should have them read by someone qualified before opening up.
 */
export function LegalPage({ kind }: { kind: Kind }) {
  const { locale } = useI18n();
  const date = useDateFormat();
  const [who, setWho] = useState<Who>({ operator: '', contact: '', host: '' });

  useEffect(() => {
    auth
      .legal()
      .then(setWho)
      .catch(() => undefined);
  }, []);

  const fr = locale === 'fr';
  const fill = (text: string) =>
    text
      .replaceAll('{operator}', who.operator || (fr ? '[éditeur à renseigner]' : '[operator to be named]'))
      .replaceAll('{contact}', who.contact || (fr ? '[contact à renseigner]' : '[contact to be named]'))
      .replaceAll('{host}', who.host || (fr ? 'notre hébergeur' : 'our hosting provider'));

  const doc = (fr ? FR : EN)[kind];
  const other: Kind = kind === 'terms' ? 'privacy' : 'terms';

  return (
    <div className="legal">
      <article className="legal__doc">
        <Link className="btn btn--link btn--sm" to="/">
          {fr ? '← Retour au studio' : '← Back to the studio'}
        </Link>
        <h1>{doc.title}</h1>
        <p className="faint">
          {fr ? 'Version du' : 'Version of'} {date.long(TERMS_VERSION)}
        </p>
        {doc.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.body.map((paragraph, i) => (
              <p key={i}>{fill(paragraph)}</p>
            ))}
          </section>
        ))}
        <p>
          <Link to={`/${other}`}>{(fr ? FR : EN)[other].title}</Link>
        </p>
      </article>
    </div>
  );
}

const FR: Record<Kind, { title: string; sections: Section[] }> = {
  terms: {
    title: 'Conditions d’utilisation',
    sections: [
      {
        title: 'Le service',
        body: [
          'Le studio est un outil qui génère des photographies de mode à partir des photos de vêtements et de mannequins que vous importez, à l’aide de modèles d’intelligence artificielle. Il est édité par {operator}.',
          'Le service est en bêta privée : l’accès se fait sur invitation, il est fourni en l’état et peut évoluer, être interrompu ou s’arrêter. Nous vous prévenons dans la mesure du possible.',
        ],
      },
      {
        title: 'Votre compte',
        body: [
          'Une invitation ouvre un compte pour une personne. Vous gardez votre mot de passe pour vous et vous êtes responsable de ce qui est fait depuis votre compte.',
        ],
      },
      {
        title: 'Vos contenus',
        body: [
          'Vous restez propriétaire des photos que vous importez et des images générées pour vous. Vous nous autorisez à les stocker et à les traiter dans le seul but de vous fournir le service.',
          'Vous garantissez détenir les droits nécessaires sur les vêtements, marques, motifs et photographies que vous importez.',
        ],
      },
      {
        title: 'Photos de personnes',
        body: [
          'Vous n’importez le visage d’une personne qu’avec son accord explicite pour cet usage, et vous devez pouvoir le prouver. Le studio vous le demande à chaque import.',
          'Sont interdits : les photos de mineurs, de personnalités publiques sans leur autorisation, les contenus sexuels, violents, haineux ou illégaux, et toute image destinée à tromper sur l’identité d’une personne ou à lui nuire.',
        ],
      },
      {
        title: 'Les images générées',
        body: [
          'Les images sont produites par des modèles d’IA tiers et peuvent comporter des erreurs, y compris sur les vêtements reproduits. Vous les vérifiez avant tout usage et vous êtes responsable de leur utilisation.',
          'Lorsque la loi l’exige, vous indiquez que ces images ont été générées ou modifiées par une intelligence artificielle.',
        ],
      },
      {
        title: 'Suspension',
        body: [
          'Nous pouvons suspendre ou fermer un compte qui ne respecte pas ces conditions, en particulier les règles sur les photos de personnes.',
        ],
      },
      {
        title: 'Responsabilité',
        body: [
          'Dans les limites permises par la loi, notre responsabilité est limitée aux dommages directs et prévisibles. Le service étant en bêta gratuite, aucune disponibilité n’est garantie.',
        ],
      },
      {
        title: 'Droit applicable et contact',
        body: ['Ces conditions sont régies par le droit français. Pour toute question : {contact}.'],
      },
    ],
  },
  privacy: {
    title: 'Politique de confidentialité',
    sections: [
      {
        title: 'Qui est responsable',
        body: [
          'Le responsable du traitement est {operator}. Pour toute question ou demande sur vos données : {contact}.',
        ],
      },
      {
        title: 'Les données traitées',
        body: [
          'Votre compte : adresse e-mail, nom, mot de passe (conservé uniquement sous forme chiffrée irréversible), langue et thème.',
          'Vos contenus : les photos de vêtements et de mannequins que vous importez, les images générées et vos réglages de séance.',
          'Des données techniques : adresse IP, navigateur, connexions et actions sur le compte, conservées pour la sécurité, ainsi que des mesures d’usage (nombre d’images, coût des rendus).',
        ],
      },
      {
        title: 'Pourquoi',
        body: [
          'Pour vous fournir le service (exécution du contrat), pour le sécuriser et prévenir les abus (intérêt légitime), et pour vous écrire au sujet de votre compte (exécution du contrat). Nous ne vendons pas vos données et ne faisons pas de publicité.',
        ],
      },
      {
        title: 'Les photos de visages',
        body: [
          'Une photo de visage est une donnée personnelle de la personne photographiée. Elle sert uniquement à générer vos images et à vérifier la ressemblance, et vous devez avoir l’accord de cette personne. Nous ne l’utilisons pas pour entraîner des modèles.',
        ],
      },
      {
        title: 'Qui y a accès',
        body: [
          'Les données sont hébergées chez {host}. Pour générer et contrôler les images, les photos et les instructions sont envoyées à Google (API Gemini) et à Anthropic (API Claude), qui les traitent pour notre compte selon leurs conditions applicables aux clients de leurs API. Les e-mails passent par notre prestataire d’envoi.',
          'Certains de ces prestataires peuvent traiter des données hors de l’Union européenne ; ces transferts sont encadrés par les garanties prévues par le RGPD, comme les clauses contractuelles types.',
        ],
      },
      {
        title: 'Combien de temps',
        body: [
          'Tant que votre compte existe. Vous pouvez supprimer votre compte depuis les Réglages : vos contenus et vos données sont alors effacés, et disparaissent des sauvegardes au plus tard 14 jours après.',
        ],
      },
      {
        title: 'Cookies',
        body: [
          'Le studio n’utilise que des cookies nécessaires : la session de connexion et une protection contre les requêtes falsifiées. Vos préférences d’affichage sont gardées dans votre navigateur. Aucun traceur publicitaire ni outil de mesure d’audience tiers.',
        ],
      },
      {
        title: 'Vos droits',
        body: [
          'Vous pouvez demander l’accès à vos données, leur rectification, leur effacement, leur portabilité, ou vous opposer à un traitement ou en demander la limitation, en écrivant à {contact}. Vous pouvez aussi adresser une réclamation à la CNIL (cnil.fr).',
        ],
      },
    ],
  },
};

const EN: Record<Kind, { title: string; sections: Section[] }> = {
  terms: {
    title: 'Terms of use',
    sections: [
      {
        title: 'The service',
        body: [
          'The studio is a tool that generates fashion photographs from the garment and model photos you upload, using artificial-intelligence models. It is operated by {operator}.',
          'The service is in private beta: access is by invitation, it is provided as is, and it may change, be interrupted or stop. We give notice where we can.',
        ],
      },
      {
        title: 'Your account',
        body: [
          'An invitation opens an account for one person. Keep your password to yourself; you are responsible for what is done from your account.',
        ],
      },
      {
        title: 'Your content',
        body: [
          'You keep ownership of the photos you upload and of the images generated for you. You allow us to store and process them only to provide the service to you.',
          'You confirm you hold the rights needed for the garments, brands, prints and photographs you upload.',
        ],
      },
      {
        title: 'Photos of people',
        body: [
          'Only upload a person’s face with their explicit agreement to this use, and be able to show it. The studio asks you to confirm this at every upload.',
          'Not allowed: photos of minors, of public figures without their permission, sexual, violent, hateful or unlawful content, and any image meant to mislead about who someone is or to harm them.',
        ],
      },
      {
        title: 'Generated images',
        body: [
          'Images are produced by third-party AI models and can contain mistakes, including in the garments they reproduce. Check them before any use; you are responsible for how you use them.',
          'Where the law requires it, say that these images were generated or altered by artificial intelligence.',
        ],
      },
      {
        title: 'Suspension',
        body: [
          'We may suspend or close an account that breaks these terms, in particular the rules on photos of people.',
        ],
      },
      {
        title: 'Liability',
        body: [
          'As far as the law allows, our liability is limited to direct, foreseeable damage. The beta is free and no availability is guaranteed.',
        ],
      },
      {
        title: 'Governing law and contact',
        body: ['These terms are governed by French law. Questions: {contact}.'],
      },
    ],
  },
  privacy: {
    title: 'Privacy notice',
    sections: [
      {
        title: 'Who is responsible',
        body: ['The controller is {operator}. For any question or request about your data: {contact}.'],
      },
      {
        title: 'What we process',
        body: [
          'Your account: email address, name, password (stored only as an irreversible hash), language and theme.',
          'Your content: the garment and model photos you upload, the generated images and your shoot settings.',
          'Technical data: IP address, browser, sign-ins and account actions, kept for security, and usage figures (number of images, cost of renders).',
        ],
      },
      {
        title: 'Why',
        body: [
          'To provide the service (performance of the contract), to secure it and prevent abuse (legitimate interest), and to write to you about your account (performance of the contract). We do not sell your data or show advertising.',
        ],
      },
      {
        title: 'Photos of faces',
        body: [
          'A photo of a face is personal data of the person in it. It is used only to generate your images and check the likeness, and you must have that person’s agreement. We do not use it to train models.',
        ],
      },
      {
        title: 'Who can access it',
        body: [
          'Data is hosted with {host}. To generate and check images, photos and instructions are sent to Google (Gemini API) and Anthropic (Claude API), which process them on our behalf under their terms for API customers. Email goes through our mail provider.',
          'Some of these providers may process data outside the European Union; such transfers rely on the safeguards the GDPR provides for, such as standard contractual clauses.',
        ],
      },
      {
        title: 'How long',
        body: [
          'For as long as your account exists. You can delete your account from Settings: your content and data are then erased, and leave the backups within 14 days.',
        ],
      },
      {
        title: 'Cookies',
        body: [
          'The studio uses only necessary cookies: your sign-in session and a protection against forged requests. Display preferences are kept in your browser. No advertising trackers or third-party analytics.',
        ],
      },
      {
        title: 'Your rights',
        body: [
          'You can ask to access, correct, erase or port your data, or object to or restrict its processing, by writing to {contact}. You can also complain to your data-protection authority (in France, the CNIL: cnil.fr).',
        ],
      },
    ],
  },
};
